import { useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/Button";

// Prevent browser from restoring scroll position on this page
window.history.scrollRestoration = "manual";

export const TermsAndConditions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const backTo = location.state?.from;
  const shouldShowBackButton = Boolean(backTo);

  useLayoutEffect(() => {
    const rootContainer = document.getElementById("root");
    if (rootContainer) {
      rootContainer.scrollTop = 0;
    }
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {shouldShowBackButton && (
        <div className="mb-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(backTo)}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            className="text-primary-600 hover:text-primary-700 !px-0"
          >
            Back
          </Button>
        </div>
      )}

      <div className="prose max-w-none">
        <h1 className="text-3xl font-bold text-theme-text-heading mb-6">Terms and Conditions</h1>

        <p className="text-sm text-theme-text-secondary mb-8">Last updated: May 12, 2026</p>

        <div className="space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              1. Acceptance of Terms
            </h2>
            <p className="text-theme-text leading-relaxed">
              By accessing and using the AchieveOS platform, you accept and agree to be bound
              by the terms and provision of this agreement. If you do not agree to abide by the
              above, please do not use this service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              2. Platform Description
            </h2>
            <p className="text-theme-text leading-relaxed">
              AchieveOS is a professional networking and mentorship platform that connects
              achievers with experienced advisors. Our platform facilitates meaningful professional
              relationships, goal-setting, and career development through structured interactions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              3. User Accounts and Registration
            </h2>
            <div className="text-theme-text leading-relaxed space-y-2">
              <p>To use our platform, you must:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain the security of your password and account</li>
                <li>Be at least 18 years of age or have parental consent</li>
                <li>Use the platform in accordance with these terms</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">4. User Conduct</h2>
            <div className="text-theme-text leading-relaxed space-y-2">
              <p>Users agree to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Maintain professional and respectful communication</li>
                <li>Respect the confidentiality of shared information</li>
                <li>Not engage in harassment, discrimination, or inappropriate behavior</li>
                <li>Not share false or misleading information</li>
                <li>Respect intellectual property rights</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              5. Privacy and Data Protection
            </h2>
            <p className="text-theme-text leading-relaxed">
              Your privacy is important to us. Please review our Privacy Policy to understand how we
              collect, use, and protect your personal information. By using our platform, you
              consent to the collection and use of your data as described in our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              6. Intellectual Property
            </h2>
            <p className="text-theme-text leading-relaxed">
              The platform and its original content, features, and functionality are owned by
              Forward Share Ventures and are protected by international copyright, trademark,
              patent, trade secret, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              7. Limitation of Liability
            </h2>
            <p className="text-theme-text leading-relaxed">
              FSV shall not be liable for any indirect, incidental, special, consequential, or
              punitive damages resulting from your access to or use of, or inability to access or
              use the platform. This includes but is not limited to damages for loss of profits,
              goodwill, use, or data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">8. Termination</h2>
            <p className="text-theme-text leading-relaxed">
              We may terminate or suspend your account and bar access to the platform immediately,
              without prior notice or liability, under our sole discretion, for any reason
              whatsoever, including without limitation if you breach the Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              9. Changes to Terms
            </h2>
            <p className="text-theme-text leading-relaxed">
              We reserve the right to modify or replace these Terms at any time. If a revision is
              material, we will provide at least 30 days notice prior to any new terms taking
              effect. What constitutes a material change will be determined at our sole discretion.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              10. Contact Information
            </h2>
            <p className="text-theme-text leading-relaxed">
              If you have any questions about these Terms and Conditions, please contact us through
              our Contact Us page or email us at hello@forwardachieve.co.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
